import { Context, Controller } from '@hotwired/stimulus';
import { confirmWithDialog } from 'src/confirm';
import {
  EVENT_TASK_INPUT_FOCUS_REQUEST,
  EVENT_TASK_INPUT_HIGHLIGHT,
  EVENT_TURBO_FETCH_REQUEST_ERROR,
  EVENT_TURBO_BEFORE_FETCH_REQUEST,
  EVENT_TURBO_BEFORE_FETCH_RESPONSE,
} from 'src/events';
import { createElement } from 'src/utils/dom-create';
import { toggleAttribute } from 'src/utils/dom-toggle';

export default class TasksFormController extends Controller<HTMLElement> {
  public static targets = ['resolve'];
  public static values = {
    'assigned-user': String,
    'current-user': String,
    'missing-answers-dialog': String,
    'not-a-participant-dialog': String,
    participants: String,
  };

  declare private resolveTargets: readonly HTMLButtonElement[];

  declare private assignedUserValue: string;
  declare private currentUserValue: string;
  declare private participantsValue: string;
  declare private missingAnswersDialogValue: string;
  declare private notAParticipantDialogValue: string;

  declare private hasMissingAnswersDialogValue: boolean;
  declare private hasNotAParticipantDialogValue: boolean;

  private lastClickedButton: HTMLButtonElement | undefined;

  constructor(context: Context) {
    super(context);

    this.onAssigned = this.onAssigned.bind(this);
    this.disableForm = this.disableForm.bind(this);
    this.enableForm = this.enableForm.bind(this);
    this.onLastClick = this.onLastClick.bind(this);
  }

  connect(): void {
    this.element.addEventListener(
      EVENT_TURBO_BEFORE_FETCH_REQUEST,
      this.disableForm,
    );
    this.element.addEventListener(
      EVENT_TURBO_BEFORE_FETCH_RESPONSE,
      this.enableForm,
    );
    this.element.addEventListener(
      EVENT_TURBO_FETCH_REQUEST_ERROR,
      this.enableForm,
    );
    this.element.addEventListener('click', this.onLastClick);

    this.updateParticipantDialog();
  }

  disconnect(): void {
    this.element.removeEventListener(
      EVENT_TURBO_BEFORE_FETCH_REQUEST,
      this.disableForm,
    );
    this.element.removeEventListener(
      EVENT_TURBO_BEFORE_FETCH_RESPONSE,
      this.enableForm,
    );
    this.element.removeEventListener(
      EVENT_TURBO_FETCH_REQUEST_ERROR,
      this.enableForm,
    );
    this.element.removeEventListener('click', this.onLastClick);
  }

  private disableForm() {
    toggleAttribute('disabled', this.element, true);
    this.element.querySelectorAll('button[type="submit"]').forEach((button) => {
      toggleAttribute('disabled', button, true);
    });

    if (!this.lastClickedButton) {
      return;
    }

    const replace = this.lastClickedButton.getAttribute(
      'data-disable-with-value',
    );
    const target =
      this.lastClickedButton.querySelector('span') || this.lastClickedButton;
    const prev = target.textContent;

    target.textContent = replace;
    this.lastClickedButton.setAttribute('data-restore-with-value', prev);
  }

  private enableForm() {
    toggleAttribute('disabled', this.element, false);
    this.element.querySelectorAll('button[type="submit"]').forEach((button) => {
      toggleAttribute('disabled', button, false);
    });

    if (!this.lastClickedButton) {
      return;
    }

    const restore = this.lastClickedButton.getAttribute(
      'data-restore-with-value',
    );
    const target =
      this.lastClickedButton.querySelector('span') || this.lastClickedButton;
    target.textContent = restore;

    this.lastClickedButton.removeAttribute('data-restore-with-value');
    this.lastClickedButton = undefined;
  }

  private onLastClick(e: Event) {
    if (!e.target || !(e.target instanceof Element)) {
      return;
    }

    if (!e.target.closest('button[type="submit"]')) {
      return;
    }

    this.lastClickedButton =
      e.target.closest('button[type="submit"]') ?? undefined;

    if (!this.lastClickedButton) {
      return;
    }

    if (this.resolveTargets.includes(this.lastClickedButton)) {
      this.onResolve(e);
    }
  }

  private onResolve(e: Event) {
    if (!this.hasMissingAnswersDialogValue || !this.lastClickedButton) {
      return;
    }

    const liveControllerElement = this.element.closest(
      '[data-controller~="tasks--live"]',
    );
    if (!liveControllerElement) {
      return;
    }

    const requiredInputs =
      liveControllerElement.querySelectorAll<HTMLElement>('ds-input[required]');

    const missingInputs: Record<string, HTMLElement> = {};
    const foundInputs: Record<string, HTMLElement> = {};

    requiredInputs.forEach((dsInput) => {
      const key = dsInput.getAttribute('ds-name');
      if (key === null) {
        return;
      }

      const inputs = dsInput.querySelectorAll<
        HTMLInputElement | HTMLTextAreaElement
      >('input,textarea');

      inputs.forEach((input) => {
        if (
          (!['radio', 'checkbox'].includes(input.type) ||
            (input as HTMLInputElement).checked) &&
          input.value &&
          input.value !== 'unknown'
        ) {
          foundInputs[key] = input;
        }
      });

      if (foundInputs[key]) {
        return;
      }

      missingInputs[key] = dsInput;
    });

    Object.keys(foundInputs).forEach((key) => delete missingInputs[key]);

    if (Object.keys(missingInputs).length === 0) {
      return;
    }

    const previousValue = this.lastClickedButton.getAttribute(
      'data-confirm-dialog',
    );
    this.lastClickedButton.setAttribute(
      'data-confirm-dialog',
      this.missingAnswersDialogValue,
    );

    // TODO: dispatch an event instead
    const dialog = document.getElementById(this.missingAnswersDialogValue);
    const list = dialog?.querySelector('[data-target="missing-answers-list"]');

    // Will be cleaned up when missing inputs is cleared

    const handler = (e: Event) => {
      if (!e.target || !(e.target instanceof Element)) {
        return;
      }

      const element = e.target.closest('[data-ds-name]');
      const dsName = element?.getAttribute('data-ds-name');

      if (!dsName) {
        return;
      }

      // Should be next frame for focus management
      setTimeout(
        () =>
          this.dispatch(EVENT_TASK_INPUT_FOCUS_REQUEST, { detail: { dsName } }),
        0,
      );
    };

    if (list) {
      list.innerHTML = '';

      Object.values(missingInputs).forEach((missingInput) => {
        const attributes = missingInput.attributes;
        const dsType = attributes.getNamedItem('ds-type')?.value;
        const dsName = attributes.getNamedItem('ds-name')?.value;

        const legend = ['radio', 'checkbox'].includes(dsType ?? '')
          ? missingInput.closest('fieldset')?.querySelector('legend')
          : null;

        const label = (
          (legend
            ? ((legend.getAttribute('data-original-label') ||
                legend.getAttribute('label') ||
                legend.textContent) ??
              attributes.getNamedItem('ds-label')?.value)
            : attributes.getNamedItem('ds-label')?.value) ?? ''
        ).trim();

        this.dispatch(EVENT_TASK_INPUT_HIGHLIGHT, {
          detail: { dsName, state: 'error' },
          prefix: '',
        });

        const button = createElement(
          'button',
          {
            class: 'link underline --primary inline-flex text-left',
            'data-ds-name': dsName ?? '',
          },
          label,
        );

        button.addEventListener('click', handler);

        const item = createElement('li', {}, button);

        list.append(item);
      });
    }

    // Open dialog
    if (!confirmWithDialog('', this.lastClickedButton)) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (previousValue) {
      this.lastClickedButton.setAttribute('data-confirm-dialog', previousValue);
    } else {
      this.lastClickedButton.removeAttribute('data-confirm-dialog');
    }
  }

  public onAssigned(event: Event) {
    if (
      !('detail' in event) ||
      typeof event.detail !== 'object' ||
      !event.detail
    ) {
      throw new Error('Expected custom event with detail property');
    }

    const stimulusAction = event as Omit<CustomEvent, 'detail'> & {
      detail: {
        userId: string;
      };
    };
    if (!('userId' in stimulusAction.detail)) {
      throw new Error(
        'Expected userId in detail, actual: ' +
          Object.keys(stimulusAction.detail).toString(),
      );
    }

    const { userId } = stimulusAction.detail;

    this.assignedUserValue = userId || '';
    this.updateParticipantDialog();
  }

  private updateParticipantDialog() {
    if (!this.hasNotAParticipantDialogValue) {
      return;
    }

    if (this.assignedUserValue == this.currentUserValue) {
      this.resolveTargets.forEach((button) => {
        if (
          button.getAttribute('data-confirm-dialog') ===
          this.notAParticipantDialogValue
        ) {
          button.removeAttribute('data-confirm');
          button.removeAttribute('data-confirm-dialog');
        }
      });
    } else {
      this.resolveTargets.forEach((button) => {
        if (button.getAttribute('data-resolve-value') !== '1') {
          return;
        }

        if (!button.hasAttribute('data-confirm-dialog')) {
          button.setAttribute('data-confirm', 'Are you sure?');
          button.setAttribute(
            'data-confirm-dialog',
            this.notAParticipantDialogValue,
          );
        }
      });
    }
  }
}
